// Camera and screen source slots, and the manager that captures the main
// slot's frames into JPEG payloads on a fixed interval.
//
// Only the main slot's frames go to the model. Other slots are local-preview
// only — except for high-quality "document scan" stills which are captured
// from a specific source by index and sent over a separate WS message type.
//
// A capability-gated "video_file" slot kind lets allowed spaces play a LOCAL
// video file and feed its decoded frames through the exact same image path as
// a camera/screen. The file never leaves the browser; the slot only ever
// emits ordinary image frames. On the wire it presents as a camera (see
// `wireSourceKind`) so the backend cannot distinguish the source — this is a
// product/UI gate, not an anti-spoofing control.

import {
  DOCUMENT_SCAN_FALLBACK_MAX_DIM,
  DOCUMENT_SCAN_JPEG_QUALITY,
  DOCUMENT_SCAN_MAX_BYTES,
  DOCUMENT_SCAN_MAX_DIM,
  DOCUMENT_SCAN_QUALITY_LADDER,
  FRAME_INTERVAL_MS,
  FRAME_MAX_DIM,
  JPEG_QUALITY,
  OBSERVER_COMPOSITE_JPEG_QUALITY,
  OBSERVER_COMPOSITE_MAX_BYTES,
  OBSERVER_COMPOSITE_MAX_HEIGHT,
  OBSERVER_COMPOSITE_MAX_WIDTH,
  OBSERVER_COMPOSITE_REQUIRED_WARNING_MS,
} from "./constants.js";
import {
  logDebug,
  setCompositeViewActive,
  setDebugCompositeView,
} from "./debug.js";
import { t } from "./i18n.js";
import { appendMessage, setState } from "./state.js";

// Base64 encode an ArrayBuffer in chunks. We avoid String.fromCharCode.apply
// on the full byte view because that hits per-engine arg-count limits for
// large buffers.
function _arrayBufferToBase64(buf) {
  const bytes = new Uint8Array(buf);
  const chunkSize = 0x8000;
  let binary = "";
  for (let i = 0; i < bytes.length; i += chunkSize) {
    const chunk = bytes.subarray(i, i + chunkSize);
    binary += String.fromCharCode.apply(null, chunk);
  }
  return btoa(binary);
}

// Map an internal slot kind to the kind reported on the wire. A local
// "video_file" source masquerades as a camera so the backend's normal media
// path (and any diagnostics dump of the start message / image frames) cannot
// tell it apart from a real camera. Camera/screen are reported unchanged.
export function wireSourceKind(kind) {
  return kind === "video_file" ? "camera" : kind;
}

export class VideoSourceSlot {
  constructor(manager) {
    this.manager = manager;
    this.id = `video-source-${manager.nextSourceId++}`;
    this.root = document.createElement("section");
    this.kind = "none";
    this.stream = null;
    this.videoEl = null;
    this.devices = [];
    this.selectedDeviceId = "";
    this.errorText = "";
    this.flashTimer = null;
    // Local-video-file state. ``objectUrl`` is the blob: URL backing the
    // preview element; it MUST be revoked on reset/replace/dispose so the
    // decoded file is released. ``fileName`` is shown in the card label.
    this.objectUrl = null;
    this.fileName = "";
    // Optional per-source crop. ``null`` means the full frame is sent to the
    // model; otherwise a normalized rectangle ``{x, y, w, h}`` in 0..1 of the
    // source video. Applied to every capture path (main, document scan, and
    // composite). Cleared whenever the underlying source changes.
    this.cropRegion = null;
  }

  get index() {
    return this.manager.slots.indexOf(this) + 1;
  }
  get isMain() {
    return this.manager.mainSourceId === this.id;
  }
  get isActive() {
    if (this.kind === "video_file") return !!this.objectUrl;
    return !!this.stream && (this.kind === "camera" || this.kind === "screen");
  }
  get label() {
    if (this.kind === "camera") return this._selectedCameraLabel() || "Camera";
    if (this.kind === "screen") return "Screen share";
    if (this.kind === "video_file")
      return this.fileName ? `Local video · ${this.fileName}` : "Local video file";
    if (this.kind === "camera_select") return "Select camera";
    if (this.kind === "camera_requesting") return "Requesting camera permission";
    if (this.kind === "screen_requesting") return "Requesting screen share";
    return "Not configured";
  }

  render() {
    this.root.className = "video-source-card";
    this.root.dataset.active = String(this.isActive);
    this.root.dataset.main = String(this.isMain);
    this.root.dataset.transmitting = String(this.manager.isTransmittingSlot(this));
    this.root.dataset.kind = this.kind;
    this.root.replaceChildren();

    const header = document.createElement("div");
    header.className = "video-source-header";
    const title = document.createElement("div");
    title.className = "video-source-title";
    const titleStrong = document.createElement("strong");
    titleStrong.textContent = `Source ${this.index}`;
    const titleSub = document.createElement("span");
    titleSub.textContent = this.label;
    title.append(titleStrong, titleSub);
    if (this.cropRegion) {
      const cropBadge = document.createElement("span");
      cropBadge.className = "video-source-crop-badge";
      cropBadge.textContent = t("area.badge");
      title.append(cropBadge);
    }

    const actions = document.createElement("div");
    actions.className = "video-source-actions";
    const mainLabel = document.createElement("label");
    mainLabel.className = "video-main-control";
    const mainRadio = document.createElement("input");
    mainRadio.type = "radio";
    mainRadio.name = "main-video-source";
    mainRadio.checked = this.isMain;
    mainRadio.addEventListener("change", () => this.manager.setMain(this.id));
    mainLabel.append(mainRadio, document.createTextNode(t("controls.main_view")));

    if (this.kind === "camera") {
      const cameraSwitch = document.createElement("select");
      cameraSwitch.className = "video-camera-switch";
      cameraSwitch.title = "Change camera";
      for (const device of this.devices) {
        const opt = document.createElement("option");
        opt.value = device.deviceId;
        opt.textContent = device.label || "Camera";
        cameraSwitch.appendChild(opt);
      }
      cameraSwitch.value = this.selectedDeviceId;
      cameraSwitch.addEventListener("change", () => {
        const nextDeviceId = cameraSwitch.value;
        if (!nextDeviceId || nextDeviceId === this.selectedDeviceId) return;
        void this._run(() => this.startCamera(nextDeviceId), "failed to switch camera");
      });
      actions.appendChild(cameraSwitch);
    }

    const resetBtn = document.createElement("button");
    resetBtn.className = "ghost video-source-reset";
    resetBtn.type = "button";
    resetBtn.textContent = t("controls.reset");
    resetBtn.addEventListener("click", () => this.manager.removeOrResetSlot(this));

    // Area-selection gear: only on an active source and only while the
    // workflow is not running (the model must not be re-framed mid-session).
    const trailing = [mainLabel];
    if (this.isActive && !this.manager.modelConnected) {
      const gearBtn = document.createElement("button");
      gearBtn.className = "ghost video-source-gear";
      gearBtn.type = "button";
      gearBtn.title = t("controls.select_area");
      gearBtn.setAttribute("aria-label", t("controls.select_area"));
      gearBtn.textContent = "⚙";
      gearBtn.dataset.active = String(!!this.cropRegion);
      gearBtn.addEventListener("click", () => this.manager.openAreaSelector(this));
      trailing.push(gearBtn);
    }
    trailing.push(resetBtn);
    actions.append(...trailing);
    header.append(title, actions);

    const body = document.createElement("div");
    body.className = "video-source-body";

    if (this.isActive) this._renderActiveBody(body);
    else if (this.kind === "camera_select") this._renderCameraSelectBody(body);
    else if (this.kind === "camera_requesting" || this.kind === "screen_requesting")
      this._renderWaitingBody(body);
    else this._renderInitialBody(body);

    this.root.append(header, body);

    if (this.errorText) {
      const err = document.createElement("p");
      err.className = "video-source-error";
      err.textContent = this.errorText;
      body.appendChild(err);
    }

    if (this.isActive) this._attach();
  }

  _renderInitialBody(body) {
    const wrap = document.createElement("div");
    wrap.className = "video-source-options";
    const text = document.createElement("p");
    text.className = "video-source-empty";
    text.textContent = "Choose a video source for this view.";
    const captureVideoBtn = document.createElement("button");
    captureVideoBtn.className = "media-btn ghost";
    captureVideoBtn.type = "button";
    captureVideoBtn.textContent = t("controls.capture_video");
    captureVideoBtn.addEventListener("click", () =>
      this._run(() => this.requestCameraSelection(), "failed to capture video"),
    );
    const shareScreenBtn = document.createElement("button");
    shareScreenBtn.className = "media-btn ghost";
    shareScreenBtn.type = "button";
    shareScreenBtn.textContent = t("controls.share_screen");
    shareScreenBtn.addEventListener("click", () =>
      this._run(() => this.startScreen(), "failed to share screen"),
    );
    wrap.append(text, captureVideoBtn, shareScreenBtn);

    // Capability-gated: only spaces the backend marked with
    // ``features.debug_video_sources`` get the local-video option. Visibility
    // is driven by that server flag, not hardcoded on.
    if (this.manager.debugVideoSourcesEnabled) {
      const fileInput = document.createElement("input");
      fileInput.type = "file";
      fileInput.accept = "video/*";
      fileInput.className = "video-source-file-input";
      fileInput.hidden = true;
      fileInput.addEventListener("change", () => {
        const file = fileInput.files && fileInput.files[0];
        // Reset so picking the same file again still fires a change event.
        fileInput.value = "";
        if (file) void this._run(() => this.insertVideoFile(file), "failed to insert video");
      });
      const insertVideoBtn = document.createElement("button");
      insertVideoBtn.className = "media-btn ghost video-source-insert-video";
      insertVideoBtn.type = "button";
      insertVideoBtn.textContent = t("controls.insert_video");
      insertVideoBtn.addEventListener("click", () => fileInput.click());
      wrap.append(insertVideoBtn, fileInput);
    }

    body.appendChild(wrap);
  }

  _renderWaitingBody(body) {
    const text = document.createElement("p");
    text.className = "video-source-empty";
    text.textContent =
      this.kind === "camera_requesting"
        ? "Waiting for browser camera permission..."
        : "Waiting for browser screen-share permission...";
    body.appendChild(text);
  }

  _renderCameraSelectBody(body) {
    const panel = document.createElement("div");
    panel.className = "video-source-select-panel";
    const label = document.createElement("label");
    label.textContent = "Camera source";
    const select = document.createElement("select");
    const placeholder = document.createElement("option");
    placeholder.value = "";
    placeholder.textContent = "Select a camera...";
    select.appendChild(placeholder);

    this.devices.forEach((device, idx) => {
      const opt = document.createElement("option");
      opt.value = device.deviceId;
      opt.textContent = device.label || `Camera ${idx + 1}`;
      select.appendChild(opt);
    });

    select.value = this.selectedDeviceId || "";
    select.addEventListener("change", () => {
      const deviceId = select.value;
      if (!deviceId) return;
      void this._run(() => this.startCamera(deviceId), "failed to start selected camera");
    });

    label.appendChild(select);
    const hint = document.createElement("p");
    hint.className = "video-source-hint";
    hint.textContent =
      "Camera permission is granted. The viewfinder starts only after a camera is selected.";
    panel.append(label, hint);
    body.appendChild(panel);
  }

  _renderActiveBody(body) {
    if (this.kind === "video_file") {
      // Reuse the element created in `insertVideoFile` so re-rendering the
      // card (which happens on many unrelated events) never resets playback
      // position. Only rebuild it if it somehow went missing.
      if (!this.videoEl) this._buildVideoFileElement();
    } else {
      this.videoEl = document.createElement("video");
      this.videoEl.className = "video-source-preview";
      this.videoEl.autoplay = true;
      this.videoEl.muted = true;
      this.videoEl.playsInline = true;
    }
    const meta = document.createElement("div");
    meta.className = "video-source-meta";
    if (this.kind === "video_file") meta.dataset.localVideo = "true";
    const transmitting = this.manager.isTransmittingSlot(this);
    if (transmitting) {
      meta.textContent = `${this.label} · transmitting to model`;
    } else if (this.isMain) {
      meta.textContent = `${this.label} · main, local preview only`;
    } else {
      meta.textContent = `${this.label} · local preview only`;
    }
    body.append(this.videoEl, meta);
  }

  async refreshCameraDevices() {
    const devices = await navigator.mediaDevices.enumerateDevices();
    this.devices = devices.filter((device) => device.kind === "videoinput");
    if (
      this.selectedDeviceId &&
      !this.devices.some((device) => device.deviceId === this.selectedDeviceId)
    ) {
      this.selectedDeviceId = "";
    }
    return this.devices;
  }

  async requestCameraSelection() {
    this.errorText = "";
    this.kind = "camera_requesting";
    this.render();
    this.manager.notify();
    setState("idle", t("status.requesting_camera"));

    let tempStream = null;
    try {
      tempStream = await navigator.mediaDevices.getUserMedia({ video: true, audio: false });
    } finally {
      if (tempStream) {
        for (const track of tempStream.getTracks()) track.stop();
      }
    }

    await this.refreshCameraDevices();
    if (this.devices.length === 0) throw new Error("No camera devices found.");

    this.kind = "camera_select";
    this.selectedDeviceId = "";
    this.render();
    this.manager.notify();
    setState("idle", t("status.cam_permission_granted"));
    logDebug("browser", "camera_permission_granted", {
      source_id: this.id,
      devices: this.devices.length,
    });
  }

  async startCamera(deviceId) {
    if (!deviceId) return;
    this.errorText = "";
    setState("idle", t("status.starting_camera"));

    if (this.devices.length === 0) await this.refreshCameraDevices();

    // Request 1920x1080 as an *ideal* — never exact — so cameras that don't
    // support that resolution still hand back their best-available capture
    // size rather than failing outright. Higher capture resolution is what
    // lets the document scanner read fine text. We also fall back to the
    // historical constraint shape (deviceId only) if the ideal request
    // fails for any reason.
    let nextStream;
    try {
      nextStream = await navigator.mediaDevices.getUserMedia({
        video: {
          deviceId: { exact: deviceId },
          width: { ideal: 1920 },
          height: { ideal: 1080 },
        },
        audio: false,
      });
    } catch (_) {
      nextStream = await navigator.mediaDevices.getUserMedia({
        video: { deviceId: { exact: deviceId } },
        audio: false,
      });
    }

    this._stopStreamOnly();
    this.stream = nextStream;
    this.kind = "camera";
    this.selectedDeviceId = deviceId;
    // A new device means a different framing; drop any prior crop.
    this.cropRegion = null;
    await this.refreshCameraDevices();
    this.render();
    this.manager.notify();

    if (this.manager.modelConnected) setState("connected", t("status.camera_ready"));
    else setState("idle", t("status.camera_ready"));

    logDebug("browser", "camera_started", {
      source_id: this.id,
      device_id: deviceId,
      main: this.isMain,
    });
  }

  async startScreen() {
    this.errorText = "";
    this.kind = "screen_requesting";
    this.render();
    this.manager.notify();
    setState("idle", t("status.requesting_screen"));

    const nextStream = await navigator.mediaDevices.getDisplayMedia({
      video: true,
      audio: false,
    });

    this._stopStreamOnly();
    this.stream = nextStream;
    this.kind = "screen";
    this.cropRegion = null;
    this.render();
    this.manager.notify();

    const track = this.stream.getVideoTracks()[0];
    if (track) {
      track.addEventListener(
        "ended",
        () => {
          this.reset();
          this.manager.render();
          this.manager.notify();
          setState("idle", t("status.screen_stopped"));
          logDebug("browser", "screen_share_ended", { source_id: this.id });
        },
        { once: true },
      );
    }

    setState("idle", t("status.screen_ready"));
    logDebug("browser", "screen_share_started", {
      source_id: this.id,
      main: this.isMain,
    });
  }

  // Local-video-file source. The file is decoded by the browser only; we
  // create an object URL, drive a persistent <video> element from it, and let
  // the existing capture loop turn its frames into ordinary image messages.
  // The original file is never uploaded.
  insertVideoFile(file) {
    if (!file) return;
    this.errorText = "";
    // Releasing any prior source (camera/screen stream or a previous file).
    this._stopStreamOnly();
    this._revokeObjectUrl();

    this.objectUrl = URL.createObjectURL(file);
    this.fileName = file.name || "Local video file";
    this.kind = "video_file";
    this.cropRegion = null;
    this.devices = [];
    this.selectedDeviceId = "";
    this._buildVideoFileElement();

    // Deterministic replay: if a session is already live, start from the top
    // immediately; otherwise hold at the first frame as a muted preview.
    if (this.manager.modelConnected) this.startVideoFilePlayback();

    this.render();
    this.manager.notify();
    setState(
      this.manager.modelConnected ? "connected" : "idle",
      t("status.video_file_ready"),
    );
    logDebug("browser", "video_file_inserted", {
      source_id: this.id,
      main: this.isMain,
    });
  }

  _buildVideoFileElement() {
    const v = document.createElement("video");
    v.className = "video-source-preview";
    v.muted = true;
    v.playsInline = true;
    v.preload = "metadata";
    v.loop = false;
    v.controls = false;
    if (this.objectUrl) v.src = this.objectUrl;
    this.videoEl = v;
    return v;
  }

  _revokeObjectUrl() {
    if (this.objectUrl) {
      try {
        URL.revokeObjectURL(this.objectUrl);
      } catch (_) {
        /* best-effort: a double-revoke or missing URL impl must not throw */
      }
      this.objectUrl = null;
    }
  }

  // Rewind + play, used when a live session starts so replay is deterministic.
  startVideoFilePlayback() {
    if (this.kind !== "video_file" || !this.videoEl) return;
    try {
      this.videoEl.currentTime = 0;
    } catch (_) {
      /* setting currentTime before metadata loads can throw — ignore */
    }
    if (typeof this.videoEl.play === "function") this.videoEl.play().catch(() => {});
  }

  pauseVideoFilePlayback() {
    if (this.kind !== "video_file" || !this.videoEl) return;
    if (typeof this.videoEl.pause === "function") {
      try {
        this.videoEl.pause();
      } catch (_) {
        /* ignore */
      }
    }
  }

  reset() {
    this._stopStreamOnly();
    this._revokeObjectUrl();
    this.kind = "none";
    this.devices = [];
    this.selectedDeviceId = "";
    this.errorText = "";
    this.fileName = "";
    this.videoEl = null;
    this.cropRegion = null;
    this.render();
    logDebug("browser", "video_source_reset", { source_id: this.id });
  }

  dispose() {
    this._stopStreamOnly();
    this._revokeObjectUrl();
    if (this.flashTimer) {
      clearTimeout(this.flashTimer);
      this.flashTimer = null;
    }
    this.root.remove();
  }

  flashUsed(durationMs = 1000) {
    const ms = Math.max(250, Math.min(3000, Number(durationMs) || 1000));
    if (this.flashTimer) {
      clearTimeout(this.flashTimer);
      this.flashTimer = null;
    }
    this.root.dataset.useFlash = "true";
    this.flashTimer = setTimeout(() => {
      delete this.root.dataset.useFlash;
      this.flashTimer = null;
    }, ms);
  }

  _attach() {
    if (!this.videoEl) return;
    if (this.kind === "video_file") {
      // The persistent element already has its blob URL as ``src``; never
      // touch srcObject here. Resume playback only once the model is live so
      // the preview holds at the first frame until a session starts.
      if (this.objectUrl && this.videoEl.src !== this.objectUrl) {
        this.videoEl.src = this.objectUrl;
      }
      if (this.manager.modelConnected) this.videoEl.play().catch(() => {});
      return;
    }
    if (!this.stream) return;
    if (this.videoEl.srcObject !== this.stream) this.videoEl.srcObject = this.stream;
    this.videoEl.play().catch(() => {});
  }

  _stopStreamOnly() {
    if (this.stream) {
      for (const track of this.stream.getTracks()) track.stop();
      this.stream = null;
    }
    if (this.videoEl) this.videoEl.srcObject = null;
  }

  _selectedCameraLabel() {
    const device = this.devices.find((d) => d.deviceId === this.selectedDeviceId);
    return device?.label || "";
  }

  async _run(action, errorLabel) {
    try {
      await action();
    } catch (e) {
      console.error(e);
      const message = e?.message || String(e);
      this.errorText = `${errorLabel}: ${message}`;
      if (!this.isActive) this.kind = "none";
      this.render();
      this.manager.notify();
      appendMessage("error", this.errorText);
      setState("error", errorLabel);
      logDebug(
        "browser",
        "video_source_error",
        { source_id: this.id, action: errorLabel, message },
        "error",
      );
    }
  }
}

export class VideoSourceManager {
  constructor({ container, canvasEl, addButton, onChange }) {
    this.container = container;
    this.canvasEl = canvasEl;
    this.addButton = addButton;
    this.onChange = onChange;
    this.onFrame = null;
    this.nextSourceId = 1;
    this.slots = [];
    this.mainSourceId = null;
    this.modelConnected = false;
    // Off until the backend's authenticated /config marks this space as
    // allowed (features.debug_video_sources). Gates the local-video option.
    this.debugVideoSourcesEnabled = false;
    // Observer video defaults to historical selected-main-source behavior.
    this.observerVideoConfig = { mode: "main_only" };
    this.compositeCanvasEl =
      typeof document !== "undefined" ? document.createElement("canvas") : null;
    this.compositeEncodeInFlight = false;
    this._lastCompositeWarningAt = new Map();
    // Area-selection modal state. Built lazily on first use.
    this.areaSelectorEl = null;
    this._areaSelectorSlot = null;
    this._areaPendingCrop = null;
    this._areaDragging = false;
    this._areaStart = null;

    if (this.addButton) {
      this.addButton.addEventListener("click", () => this.addSlot());
    }

    this.addSlot({ silent: true });
    this.frameTimer = setInterval(() => void this._snapMain(), FRAME_INTERVAL_MS);
  }

  setModelConnected(connected) {
    this.modelConnected = !!connected;
    // Area selection is an idle-only affordance: a running session must never
    // be re-framed underneath the model. Close any open selector on connect.
    if (this.modelConnected) this._closeAreaSelector();
    // Tie local-video playback to the session lifecycle: rewind + play when a
    // session starts (deterministic replay), pause when it ends. Camera and
    // screen sources are live and unaffected.
    for (const slot of this.slots) {
      if (this.modelConnected) slot.startVideoFilePlayback();
      else slot.pauseVideoFilePlayback();
    }
    this.render();
    this.notify();
  }

  // Toggle the capability-gated local-video option. Driven by the backend's
  // authenticated /config response, not hardcoded in the UI.
  setDebugVideoSourcesEnabled(enabled) {
    const next = !!enabled;
    if (this.debugVideoSourcesEnabled === next) return;
    this.debugVideoSourcesEnabled = next;
    this.render();
  }

  setObserverVideoConfig(config) {
    this.observerVideoConfig = this._normalizeObserverVideoConfig(config);
    // The composite "video sent to the model" debug preview only makes sense
    // for composite-vertical workflows (Hellmann Expert); hide it otherwise.
    setCompositeViewActive?.(this._isCompositeMode());
    this.render();
    this.notify();
  }

  _normalizeObserverVideoConfig(config) {
    if (!config || config.mode !== "composite_vertical") {
      return { mode: "main_only" };
    }
    const requiredSources = Array.isArray(config.required_sources)
      ? config.required_sources
          .map((source) => ({
            index: Math.max(1, Number(source?.index) || 1),
            label: String(source?.label || `Source ${source?.index || ""}`).trim(),
            role: String(source?.role || "").trim(),
          }))
          .filter((source) => source.index >= 1)
      : [];
    return {
      mode: "composite_vertical",
      layout: config.layout || "vertical",
      required_sources: requiredSources,
      max_width: Number(config.max_width) || OBSERVER_COMPOSITE_MAX_WIDTH,
      max_height: Number(config.max_height) || OBSERVER_COMPOSITE_MAX_HEIGHT,
      jpeg_quality:
        typeof config.jpeg_quality === "number"
          ? config.jpeg_quality
          : OBSERVER_COMPOSITE_JPEG_QUALITY,
    };
  }

  _isCompositeMode() {
    return this.observerVideoConfig?.mode === "composite_vertical";
  }

  isTransmittingSlot(slot) {
    if (this._isCompositeMode()) {
      const required = this.observerVideoConfig.required_sources || [];
      return (
        !!slot &&
        this.modelConnected &&
        required.some((source) => Number(source.index) === slot.index) &&
        slot.isActive &&
        typeof this.onFrame === "function"
      );
    }
    return (
      !!slot &&
      this.modelConnected &&
      slot.id === this.mainSourceId &&
      slot.isActive &&
      typeof this.onFrame === "function"
    );
  }

  addSlot({ silent = false } = {}) {
    const slot = new VideoSourceSlot(this);
    this.slots.push(slot);
    if (!this.mainSourceId) this.mainSourceId = slot.id;
    this.render();
    this.notify();
    if (!silent) {
      logDebug("browser", "video_source_added", {
        source_id: slot.id,
        count: this.slots.length,
      });
    }
    return slot;
  }

  render() {
    if (!this.container) return;
    this.container.replaceChildren();
    for (const slot of this.slots) {
      slot.render();
      this.container.appendChild(slot.root);
    }
    this.updateToolbar();
  }

  updateToolbar() {
    if (this.addButton) this.addButton.disabled = !this.hasActiveSources();
  }

  notify() {
    this.updateToolbar();
    if (this.onChange) {
      this.onChange({
        active_sources: this.activeSourceCount(),
        main_source_id: this.mainSourceId,
        mode: this.currentMode(),
      });
    }
  }

  setMain(sourceId) {
    if (!this.slots.some((slot) => slot.id === sourceId)) return;
    this.mainSourceId = sourceId;
    this.render();
    this.notify();
    logDebug("browser", "video_source_main_changed", {
      source_id: sourceId,
      mode: this.currentMode(),
    });
  }

  removeOrResetSlot(slot) {
    const wasMain = slot.id === this.mainSourceId;
    if (this.slots.length <= 1) {
      slot.reset();
      this.mainSourceId = slot.id;
      this.render();
      this.notify();
      return;
    }
    slot.dispose();
    this.slots = this.slots.filter((candidate) => candidate !== slot);
    if (wasMain) this.mainSourceId = this.slots[0]?.id || null;
    this.render();
    this.notify();
    logDebug("browser", "video_source_removed", {
      source_id: slot.id,
      remaining: this.slots.length,
      main_source_id: this.mainSourceId,
    });
  }

  resetAllToSingle() {
    for (const slot of this.slots) slot.dispose();
    this.slots = [];
    this.mainSourceId = null;
    const slot = new VideoSourceSlot(this);
    this.slots.push(slot);
    this.mainSourceId = slot.id;
    this.render();
    this.notify();
    logDebug("browser", "video_sources_reset_all");
  }

  hasActiveSources() {
    return this.slots.some((slot) => slot.isActive);
  }

  activeSourceCount() {
    return this.slots.filter((slot) => slot.isActive).length;
  }

  getMainSlot() {
    return this.slots.find((slot) => slot.id === this.mainSourceId) || null;
  }

  currentMode() {
    const main = this.getMainSlot();
    if (!main || !main.isActive) return "none";
    // A local video file reports as a camera so the start-message ``mode``
    // the backend sees is an ordinary value, not the synthetic source kind.
    return wireSourceKind(main.kind);
  }

  captureFrameById(sourceId, { flashUsed = true } = {}) {
    const slot = this.slots.find((candidate) => candidate.id === sourceId);
    return this._captureSlotFrame(slot, { flashUsed });
  }

  captureFrameByIndex(
    sourceIndex,
    { flashUsed = true, maxDim = FRAME_MAX_DIM, jpegQuality = JPEG_QUALITY } = {},
  ) {
    const index = Math.max(1, Number(sourceIndex) || 2);
    const slot = this.slots[index - 1] || null;
    return this._captureSlotFrame(slot, { flashUsed, maxDim, jpegQuality });
  }

  // High-quality, async, Blob-based capture for the document scanner. Uses
  // canvas.toBlob (not toDataURL) so we don't pay the base64 round-trip on
  // the encode side, and so we can measure exact byte size before the wire
  // format is built. Falls back to lower quality and then to a smaller
  // maxDim if the encoded JPEG exceeds the size cap.
  async captureDocumentFrameByIndex(
    sourceIndex,
    {
      maxDim = DOCUMENT_SCAN_MAX_DIM,
      jpegQuality = DOCUMENT_SCAN_JPEG_QUALITY,
      maxBytes = DOCUMENT_SCAN_MAX_BYTES,
      flashUsed = true,
    } = {},
  ) {
    const index = Math.max(1, Number(sourceIndex) || 2);
    const slot = this.slots[index - 1] || null;
    if (!slot || !slot.isActive || !slot.videoEl) return null;
    const v = slot.videoEl;
    if (!v.videoWidth || !v.videoHeight) return null;

    // Build a list of (maxDim, quality) attempts. We always try the
    // requested config first; subsequent attempts trade off quality before
    // resolution to preserve legibility.
    const attempts = [];
    for (const q of [jpegQuality, ...DOCUMENT_SCAN_QUALITY_LADDER]) {
      if (!attempts.some((a) => a.maxDim === maxDim && a.quality === q)) {
        attempts.push({ maxDim, quality: q });
      }
    }
    if (DOCUMENT_SCAN_FALLBACK_MAX_DIM < maxDim) {
      for (const q of DOCUMENT_SCAN_QUALITY_LADDER) {
        attempts.push({ maxDim: DOCUMENT_SCAN_FALLBACK_MAX_DIM, quality: q });
      }
    }

    const src = this._slotSourceRect(slot);
    let lastBlob = null;
    let lastDims = null;
    for (const { maxDim: dim, quality } of attempts) {
      const dims = this._sizeFor(src.sw, src.sh, dim);
      this.canvasEl.width = dims.w;
      this.canvasEl.height = dims.h;
      const ctx = this.canvasEl.getContext("2d");
      ctx.drawImage(v, src.sx, src.sy, src.sw, src.sh, 0, 0, dims.w, dims.h);
      const blob = await new Promise((resolve) => {
        try {
          this.canvasEl.toBlob((b) => resolve(b), "image/jpeg", quality);
        } catch (_) {
          resolve(null);
        }
      });
      if (!blob) continue;
      lastBlob = blob;
      lastDims = dims;
      if (blob.size <= maxBytes) break;
    }
    if (!lastBlob) return null;
    if (lastBlob.size > DOCUMENT_SCAN_MAX_BYTES) {
      logDebug(
        "browser",
        "document_scan_too_large",
        { byte_length: lastBlob.size, max_bytes: DOCUMENT_SCAN_MAX_BYTES },
        "warn",
      );
      return null;
    }

    const arrayBuf = await lastBlob.arrayBuffer();
    const b64 = _arrayBufferToBase64(arrayBuf);

    if (flashUsed) slot.flashUsed(1000);

    return {
      mime_type: "image/jpeg",
      data: b64,
      source_id: slot.id,
      source_kind: wireSourceKind(slot.kind),
      source_index: index,
      width: lastDims.w,
      height: lastDims.h,
      byte_length: lastBlob.size,
      purpose: "document_scan",
    };
  }

  _sizeFor(srcW, srcH, maxDim) {
    let w = srcW;
    let h = srcH;
    if (Math.max(w, h) > maxDim) {
      const k = maxDim / Math.max(w, h);
      w = Math.round(w * k);
      h = Math.round(h * k);
    }
    return { w, h };
  }

  // Look up a slot by 1-based index without capturing. Used by LiveSession
  // to decide whether to attempt a scan at all.
  hasActiveSourceAt(sourceIndex) {
    const index = Math.max(1, Number(sourceIndex) || 1);
    const slot = this.slots[index - 1] || null;
    return !!(slot && slot.isActive);
  }

  observerVideoReady(config = this.observerVideoConfig) {
    const cfg = this._normalizeObserverVideoConfig(config);
    if (cfg.mode !== "composite_vertical") return true;
    return this._missingCompositeSource(cfg.required_sources || []) === null;
  }

  warnRequiredCompositeSources(config = this.observerVideoConfig) {
    const cfg = this._normalizeObserverVideoConfig(config);
    if (cfg.mode !== "composite_vertical") return;
    const missing = this._missingCompositeSource(cfg.required_sources || []);
    if (missing) this._warnCompositeSourceMissing(missing.sourceConfig, missing.reason);
  }

  _missingCompositeSource(sourceConfigs) {
    for (const sourceConfig of sourceConfigs || []) {
      const index = Math.max(1, Number(sourceConfig.index) || 1);
      const slot = this.slots[index - 1] || null;
      if (!slot || !slot.isActive || !slot.videoEl) {
        return { sourceConfig, reason: "missing_or_inactive" };
      }
      const v = slot.videoEl;
      if (!v.videoWidth || !v.videoHeight) {
        return { sourceConfig, reason: "no_video_dimensions" };
      }
    }
    return null;
  }

  _compositeWarningText(sourceConfig) {
    const index = Math.max(1, Number(sourceConfig?.index) || 1);
    const role = String(sourceConfig?.role || "").toLowerCase();
    if (index === 2 || role === "lfs_screen") {
      return t("status.hellmann_expert_source_2_required");
    }
    if (index === 1 || role === "workstation") {
      return t("status.hellmann_expert_source_1_required");
    }
    return `${sourceConfig?.label || `Source ${index}`} is required for this workflow.`;
  }

  _warnCompositeSourceMissing(sourceConfig, reason) {
    const index = Math.max(1, Number(sourceConfig?.index) || 1);
    const key = `source-${index}:${reason}`;
    const now = Date.now();
    const last = this._lastCompositeWarningAt.get(key) || 0;
    if (now - last < OBSERVER_COMPOSITE_REQUIRED_WARNING_MS) return;
    this._lastCompositeWarningAt.set(key, now);
    const message = this._compositeWarningText(sourceConfig);
    setState(this.modelConnected ? "connected" : "idle", message);
    appendMessage("system", message);
    console.warn(message);
    logDebug(
      "browser",
      "observer_composite_source_missing",
      { source_index: index, role: sourceConfig?.role || null, reason, message },
      "warn",
    );
  }

  // ---- Area selection (per-source crop) modal -------------------------------
  //
  // Opens a modal showing the chosen source at full frame and lets the operator
  // drag a rectangle to pick the region that should be sent to the model. The
  // selection is stored on the slot as a normalized ``cropRegion`` and applied
  // by every capture path. Only available while the workflow is NOT running.

  openAreaSelector(slot) {
    if (this.modelConnected) {
      setState("connected", t("area.only_when_idle"));
      return;
    }
    if (!slot || !slot.isActive || !slot.videoEl) return;
    if (typeof document === "undefined") return;
    this._buildAreaSelector();
    if (!this.areaSelectorEl) return;
    this._refreshAreaSelectorText();

    this._areaSelectorSlot = slot;
    const vid = this._areaSelectorVideo;
    try {
      vid.pause?.();
    } catch (_) {
      /* ignore */
    }
    vid.srcObject = null;
    vid.removeAttribute("src");
    if (slot.kind === "video_file" && slot.objectUrl) {
      vid.src = slot.objectUrl;
    } else if (slot.stream) {
      vid.srcObject = slot.stream;
    }
    if (typeof vid.play === "function") vid.play().catch(() => {});

    this._areaPendingCrop = slot.cropRegion ? { ...slot.cropRegion } : null;
    this._areaDragging = false;
    this._areaStart = null;
    this.areaSelectorEl.hidden = false;
    // Render the existing selection once the modal has laid out.
    this._renderAreaRect();
    if (typeof requestAnimationFrame === "function") {
      requestAnimationFrame(() => this._renderAreaRect());
    }
    logDebug("browser", "area_selector_opened", {
      source_index: slot.index,
      had_crop: !!slot.cropRegion,
    });
  }

  _buildAreaSelector() {
    if (this.areaSelectorEl || typeof document === "undefined") return;

    const ghost = (label, cls) => {
      const b = document.createElement("button");
      b.type = "button";
      b.className = cls;
      b.textContent = label;
      return b;
    };

    const overlay = document.createElement("div");
    overlay.className = "area-selector-overlay";
    overlay.hidden = true;

    const card = document.createElement("div");
    card.className = "area-selector-card";

    const head = document.createElement("div");
    head.className = "area-selector-head";
    const title = document.createElement("strong");
    title.textContent = t("area.title");
    const hint = document.createElement("p");
    hint.textContent = t("area.hint");
    head.append(title, hint);

    const stage = document.createElement("div");
    stage.className = "area-selector-stage";
    const video = document.createElement("video");
    video.className = "area-selector-video";
    video.muted = true;
    video.playsInline = true;
    video.autoplay = true;
    const rect = document.createElement("div");
    rect.className = "area-selector-rect";
    rect.hidden = true;
    stage.append(video, rect);

    const actions = document.createElement("div");
    actions.className = "area-selector-actions";
    const resetBtn = ghost(t("area.reset"), "ghost");
    const spacer = document.createElement("div");
    spacer.className = "area-selector-spacer";
    const cancelBtn = ghost(t("area.cancel"), "ghost");
    const applyBtn = ghost(t("area.apply"), "primary");
    actions.append(resetBtn, spacer, cancelBtn, applyBtn);

    card.append(head, stage, actions);
    overlay.append(card);
    document.body.appendChild(overlay);

    this.areaSelectorEl = overlay;
    this._areaSelectorVideo = video;
    this._areaSelectorStage = stage;
    this._areaSelectorRect = rect;
    // Kept so the static labels can be re-localized on each open (the modal is
    // built once and reused, but the language may change between opens).
    this._areaSelectorText = {
      title,
      hint,
      reset: resetBtn,
      cancel: cancelBtn,
      apply: applyBtn,
    };

    stage.addEventListener("pointerdown", (e) => this._areaPointerDown(e));
    stage.addEventListener("pointermove", (e) => this._areaPointerMove(e));
    if (typeof window !== "undefined") {
      window.addEventListener("pointerup", (e) => this._areaPointerUp(e));
    }
    resetBtn.addEventListener("click", () => this._applyAreaSelection(null));
    cancelBtn.addEventListener("click", () => this._closeAreaSelector());
    applyBtn.addEventListener("click", () => this._applyAreaSelection(this._areaPendingCrop));
    overlay.addEventListener("pointerdown", (e) => {
      if (e.target === overlay) this._closeAreaSelector();
    });
  }

  _refreshAreaSelectorText() {
    const refs = this._areaSelectorText;
    if (!refs) return;
    refs.title.textContent = t("area.title");
    refs.hint.textContent = t("area.hint");
    refs.reset.textContent = t("area.reset");
    refs.cancel.textContent = t("area.cancel");
    refs.apply.textContent = t("area.apply");
  }

  _areaPointFromEvent(e) {
    const video = this._areaSelectorVideo;
    if (!video || typeof video.getBoundingClientRect !== "function") return null;
    const vr = video.getBoundingClientRect();
    if (!vr.width || !vr.height) return null;
    const x = Math.min(1, Math.max(0, (e.clientX - vr.left) / vr.width));
    const y = Math.min(1, Math.max(0, (e.clientY - vr.top) / vr.height));
    return { x, y };
  }

  _areaPointerDown(e) {
    if (!this.areaSelectorEl || this.areaSelectorEl.hidden) return;
    const p = this._areaPointFromEvent(e);
    if (!p) return;
    if (typeof e.preventDefault === "function") e.preventDefault();
    this._areaCropBeforeDrag = this._areaPendingCrop;
    this._areaDragging = true;
    this._areaStart = p;
    this._areaPendingCrop = { x: p.x, y: p.y, w: 0, h: 0 };
    this._renderAreaRect();
  }

  _areaPointerMove(e) {
    if (!this._areaDragging) return;
    const p = this._areaPointFromEvent(e);
    if (!p) return;
    const s = this._areaStart;
    this._areaPendingCrop = {
      x: Math.min(s.x, p.x),
      y: Math.min(s.y, p.y),
      w: Math.abs(p.x - s.x),
      h: Math.abs(p.y - s.y),
    };
    this._renderAreaRect();
  }

  _areaPointerUp() {
    if (!this._areaDragging) return;
    this._areaDragging = false;
    const c = this._areaPendingCrop;
    // Ignore an accidental click / tiny drag — keep whatever was there before.
    if (!c || c.w < 0.02 || c.h < 0.02) {
      this._areaPendingCrop = this._areaCropBeforeDrag || null;
      this._renderAreaRect();
    }
  }

  _renderAreaRect() {
    const rectEl = this._areaSelectorRect;
    const video = this._areaSelectorVideo;
    const stage = this._areaSelectorStage;
    if (!rectEl || !video || !stage) return;
    const crop = this._areaPendingCrop;
    if (
      !crop ||
      typeof video.getBoundingClientRect !== "function" ||
      typeof stage.getBoundingClientRect !== "function"
    ) {
      rectEl.hidden = true;
      return;
    }
    const vr = video.getBoundingClientRect();
    const sr = stage.getBoundingClientRect();
    if (!vr.width || !vr.height) {
      rectEl.hidden = true;
      return;
    }
    rectEl.style.left = `${vr.left - sr.left + crop.x * vr.width}px`;
    rectEl.style.top = `${vr.top - sr.top + crop.y * vr.height}px`;
    rectEl.style.width = `${crop.w * vr.width}px`;
    rectEl.style.height = `${crop.h * vr.height}px`;
    rectEl.hidden = false;
  }

  _applyAreaSelection(crop) {
    const slot = this._areaSelectorSlot;
    this._closeAreaSelector();
    if (!slot) return;
    const idx = slot.index;
    const idleState = this.modelConnected ? "connected" : "idle";
    if (crop && crop.w >= 0.02 && crop.h >= 0.02) {
      const clamp01 = (n) => Math.min(1, Math.max(0, Number(n) || 0));
      slot.cropRegion = {
        x: clamp01(crop.x),
        y: clamp01(crop.y),
        w: clamp01(crop.w),
        h: clamp01(crop.h),
      };
      const msg = t("status.area_selected", { n: idx });
      setState(idleState, msg);
      appendMessage("system", msg);
      logDebug("browser", "source_area_selected", {
        source_index: idx,
        crop: slot.cropRegion,
      });
    } else {
      slot.cropRegion = null;
      const msg = t("status.area_cleared", { n: idx });
      setState(idleState, msg);
      appendMessage("system", msg);
      logDebug("browser", "source_area_cleared", { source_index: idx });
    }
    this.render();
    this.notify();
  }

  _closeAreaSelector() {
    if (!this.areaSelectorEl) return;
    this.areaSelectorEl.hidden = true;
    const vid = this._areaSelectorVideo;
    if (vid) {
      try {
        vid.pause?.();
      } catch (_) {
        /* ignore */
      }
      vid.srcObject = null;
      vid.removeAttribute("src");
    }
    if (this._areaSelectorRect) this._areaSelectorRect.hidden = true;
    this._areaSelectorSlot = null;
    this._areaDragging = false;
    this._areaStart = null;
    this._areaPendingCrop = null;
  }

  // Snapshot of currently-recordable sources for the local-only recorder.
  // Returns a plain array of plain objects; the underlying slot is NOT
  // exposed so the recorder cannot accidentally mutate slot state. Stream
  // and videoEl references are still passed through (the recorder needs the
  // MediaStream to drive a MediaRecorder); the recorder must treat them as
  // read-only borrowed references.
  getRecordableSources() {
    const out = [];
    for (const slot of this.slots) {
      if (!slot.isActive) continue;
      if (!slot.stream || !slot.videoEl) continue;
      if (slot.kind !== "camera" && slot.kind !== "screen") continue;

      let trackSettings = null;
      try {
        const tracks = slot.stream.getVideoTracks
          ? slot.stream.getVideoTracks()
          : [];
        const track = tracks && tracks[0];
        if (track && typeof track.getSettings === "function") {
          trackSettings = track.getSettings();
        }
      } catch (_) {
        trackSettings = null;
      }

      const v = slot.videoEl;
      const width =
        (v && v.videoWidth) ||
        (trackSettings && trackSettings.width) ||
        0;
      const height =
        (v && v.videoHeight) ||
        (trackSettings && trackSettings.height) ||
        0;

      out.push({
        id: slot.id,
        index: slot.index,
        kind: slot.kind,
        label: slot.label,
        isMain: slot.isMain,
        stream: slot.stream,
        videoEl: v,
        width,
        height,
        trackSettings,
      });
    }
    return out;
  }

  async _snapMain() {
    if (!this.onFrame) return;
    if (this._isCompositeMode()) {
      if (this.compositeEncodeInFlight) return;
      this.compositeEncodeInFlight = true;
      try {
        const cfg = this.observerVideoConfig;
        const frame = await this.captureCompositeFrameByIndices(cfg.required_sources || [], {
          layout: cfg.layout,
          maxWidth: cfg.max_width,
          maxHeight: cfg.max_height,
          jpegQuality: cfg.jpeg_quality,
        });
        if (frame) this.onFrame(frame);
      } finally {
        this.compositeEncodeInFlight = false;
      }
      return;
    }
    const slot = this.getMainSlot();
    const frame = this._captureSlotFrame(slot, { flashUsed: false });
    if (frame) this.onFrame(frame);
  }

  _compositeCanvas() {
    if (!this.compositeCanvasEl) {
      this.compositeCanvasEl = document.createElement("canvas");
    }
    return this.compositeCanvasEl;
  }

  _drawVideoContain(ctx, videoEl, rect, srcRect = null) {
    const fullW = videoEl.videoWidth || 0;
    const fullH = videoEl.videoHeight || 0;
    if (!fullW || !fullH) return;
    const src = srcRect || { sx: 0, sy: 0, sw: fullW, sh: fullH };
    const srcW = src.sw || 0;
    const srcH = src.sh || 0;
    if (!srcW || !srcH) return;
    const scale = Math.min(rect.w / srcW, rect.h / srcH);
    const dw = Math.round(srcW * scale);
    const dh = Math.round(srcH * scale);
    const dx = Math.round(rect.x + (rect.w - dw) / 2);
    const dy = Math.round(rect.y + (rect.h - dh) / 2);
    ctx.drawImage(videoEl, src.sx, src.sy, src.sw, src.sh, dx, dy, dw, dh);
  }

  // Pixel source rectangle for ``slot`` honoring its optional crop region.
  // Returns the full frame when no crop is set.
  _slotSourceRect(slot) {
    const v = slot && slot.videoEl;
    const vw = (v && v.videoWidth) || 0;
    const vh = (v && v.videoHeight) || 0;
    const crop = slot && slot.cropRegion;
    if (!crop || !vw || !vh) return { sx: 0, sy: 0, sw: vw, sh: vh };
    const clamp01 = (n) => Math.min(1, Math.max(0, Number(n) || 0));
    const sx = Math.round(clamp01(crop.x) * vw);
    const sy = Math.round(clamp01(crop.y) * vh);
    let sw = Math.round(clamp01(crop.w) * vw);
    let sh = Math.round(clamp01(crop.h) * vh);
    sw = Math.max(1, Math.min(sw, vw - sx));
    sh = Math.max(1, Math.min(sh, vh - sy));
    return { sx, sy, sw, sh };
  }

  _drawCompositeLabel(ctx, text, rect) {
    const label = String(text || "").trim();
    if (!label) return;
    ctx.save();
    const fontSize = Math.max(16, Math.round(Math.min(rect.w, rect.h) * 0.035));
    ctx.font = `600 ${fontSize}px system-ui, -apple-system, BlinkMacSystemFont, Segoe UI, sans-serif`;
    ctx.textBaseline = "top";
    const paddingX = Math.round(fontSize * 0.55);
    const paddingY = Math.round(fontSize * 0.35);
    const metrics = ctx.measureText(label);
    const boxW = Math.min(rect.w - 16, Math.ceil(metrics.width + paddingX * 2));
    const boxH = Math.ceil(fontSize + paddingY * 2);
    const x = rect.x + 8;
    const y = rect.y + 8;
    ctx.fillStyle = "rgba(0, 0, 0, 0.70)";
    ctx.fillRect(x, y, boxW, boxH);
    ctx.fillStyle = "#ffffff";
    ctx.fillText(label, x + paddingX, y + paddingY);
    ctx.restore();
  }

  async _canvasToJpegBlob(canvas, quality) {
    if (typeof canvas.toBlob === "function") {
      return new Promise((resolve) => {
        try {
          canvas.toBlob((blob) => resolve(blob), "image/jpeg", quality);
        } catch (_) {
          resolve(null);
        }
      });
    }
    // Last-resort fallback for older browsers/tests. Composite capture prefers
    // toBlob so large frames avoid the encode-side base64 round trip.
    try {
      const dataUrl = canvas.toDataURL("image/jpeg", quality);
      const b64 = dataUrl.split(",", 2)[1] || "";
      const binary = atob(b64);
      const bytes = new Uint8Array(binary.length);
      for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
      return new Blob([bytes], { type: "image/jpeg" });
    } catch (_) {
      return null;
    }
  }

  async captureCompositeFrameByIndices(
    sourceConfigs,
    {
      layout = "vertical",
      maxWidth = OBSERVER_COMPOSITE_MAX_WIDTH,
      maxHeight = OBSERVER_COMPOSITE_MAX_HEIGHT,
      jpegQuality = OBSERVER_COMPOSITE_JPEG_QUALITY,
    } = {},
  ) {
    if (layout !== "vertical") return null;
    const configs = Array.isArray(sourceConfigs) ? sourceConfigs : [];
    if (configs.length < 2) return null;

    const missing = this._missingCompositeSource(configs);
    if (missing) {
      this._warnCompositeSourceMissing(missing.sourceConfig, missing.reason);
      return null;
    }

    const width = Math.max(320, Math.round(Number(maxWidth) || OBSERVER_COMPOSITE_MAX_WIDTH));
    const height = Math.max(320, Math.round(Number(maxHeight) || OBSERVER_COMPOSITE_MAX_HEIGHT));
    const canvas = this._compositeCanvas();
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext("2d");
    if (!ctx) return null;

    ctx.fillStyle = "#050505";
    ctx.fillRect(0, 0, width, height);

    const topH = Math.floor(height / 2);
    const rects = [
      { x: 0, y: 0, w: width, h: topH },
      { x: 0, y: topH, w: width, h: height - topH },
    ];

    const usedIndices = [];
    for (let i = 0; i < 2; i++) {
      const sourceConfig = configs[i];
      const index = Math.max(1, Number(sourceConfig.index) || 1);
      const slot = this.slots[index - 1];
      const rect = rects[i];
      this._drawVideoContain(ctx, slot.videoEl, rect, this._slotSourceRect(slot));
      this._drawCompositeLabel(ctx, sourceConfig.label || `Source ${index}`, rect);
      usedIndices.push(index);
    }

    const blob = await this._canvasToJpegBlob(canvas, jpegQuality);
    if (!blob) return null;
    if (blob.size > OBSERVER_COMPOSITE_MAX_BYTES) {
      logDebug(
        "browser",
        "observer_composite_too_large",
        { byte_length: blob.size, max_bytes: OBSERVER_COMPOSITE_MAX_BYTES },
        "warn",
      );
      return null;
    }
    const arrayBuf = await blob.arrayBuffer();
    const b64 = _arrayBufferToBase64(arrayBuf);

    const frame = {
      mime_type: "image/jpeg",
      data: b64,
      source_id: "composite",
      source_kind: "composite",
      is_main: true,
      width,
      height,
      source_indices: usedIndices,
      purpose: "observer_composite",
      byte_length: blob.size,
    };
    // Mirror every produced composite frame into the debug "video sent to the
    // model" preview, even if backpressure later drops this particular frame.
    setDebugCompositeView?.(frame);
    return frame;
  }

  _captureSlotFrame(
    slot,
    { flashUsed = false, maxDim = FRAME_MAX_DIM, jpegQuality = JPEG_QUALITY } = {},
  ) {
    if (!slot || !slot.isActive || !slot.videoEl) return null;
    const v = slot.videoEl;
    if (!v.videoWidth || !v.videoHeight) return null;

    const src = this._slotSourceRect(slot);
    let w = src.sw;
    let h = src.sh;
    if (Math.max(w, h) > maxDim) {
      const k = maxDim / Math.max(w, h);
      w = Math.round(w * k);
      h = Math.round(h * k);
    }

    this.canvasEl.width = w;
    this.canvasEl.height = h;
    const ctx = this.canvasEl.getContext("2d");
    ctx.drawImage(v, src.sx, src.sy, src.sw, src.sh, 0, 0, w, h);
    const dataUrl = this.canvasEl.toDataURL("image/jpeg", jpegQuality);
    const b64 = dataUrl.split(",", 2)[1] || "";

    if (flashUsed) slot.flashUsed(1000);

    return {
      mime_type: "image/jpeg",
      data: b64,
      source_id: slot.id,
      source_kind: wireSourceKind(slot.kind),
      is_main: slot.id === this.mainSourceId,
      width: w,
      height: h,
    };
  }
}
